import { format } from 'date-fns';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useCapital } from '@/hooks/queries/useAnalytics';
import { formatCurrency, formatExpirationDate } from '@/lib/formatters';
import type { CapitalResponse } from '@/lib/api/client';

/**
 * `today` is a YYYY-MM-DD day label, defaulting to the local calendar day, and is a prop only so
 * a test can pin it — the view stays props-driven and reads no clock of its own beyond this
 * default.
 */
export function CashHeldTileView({
  data,
  today = format(new Date(), 'yyyy-MM-dd'),
}: {
  data: CapitalResponse;
  today?: string;
}) {
  const { held, account } = data;
  // The FIRST expiration still ahead, not the first row. An expiration that has passed stays in
  // byExpiration until the sweep closes its legs, and "frees up on Sep 25" read on Sep 26 is a
  // stale claim about money that is already free. byExpiration is ascending, so the first match
  // is the nearest one; a day label compares correctly as a string.
  const nextFreeUp = held.byExpiration.find((entry) => entry.expiration >= today) ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cash &amp; held</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Cash available</span>
          <span data-testid="cash-available" className="font-semibold text-gray-900">
            {account && account.cashAvailableForTrading != null
              ? formatCurrency(account.cashAvailableForTrading, { cents: false })
              : 'Schwab unavailable'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Held in options</span>
          <span data-testid="held-total" className="font-semibold text-gray-900">
            {formatCurrency(held.total, { cents: false })}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Next free-up</span>
          <span data-testid="next-free-up" className="font-semibold text-gray-900">
            {nextFreeUp
              ? `${formatCurrency(nextFreeUp.held, { cents: false })} on ${formatExpirationDate(nextFreeUp.expiration)}`
              : '—'}
          </span>
        </div>
        {account?.kind === 'MARGIN' && account.optionBuyingPower != null && held.total != null && (
          <div data-testid="margin-gap" className="text-sm text-gray-500 border-t border-gray-100 pt-3">
            Schwab option buying power{' '}
            <span className="font-semibold text-gray-900">
              {formatCurrency(account.optionBuyingPower, { cents: false })}
            </span>{' '}
            &middot; textbook held{' '}
            <span className="font-semibold text-gray-900">{formatCurrency(held.total, { cents: false })}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function CashHeldTile() {
  const { data, isLoading, isError } = useCapital();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cash &amp; held</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cash &amp; held</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Could not load</p>
        </CardContent>
      </Card>
    );
  }

  return <CashHeldTileView data={data} />;
}
